/**
 * @package   EGI / Frontend — cart
 * @author    Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version   1.0.0 (M-EGI-385 passo 3)
 * @date      2026-07-05
 * @purpose   Modulo pagina /cart (design ARCH_CART.md §4, pattern gemello di
 *            wishlist/page.ts): item SSR (il cart è server-only, ADR-C7 —
 *            niente template guest); qui SOLO rimozione ("Torna in wishlist",
 *            T2 ADR-C2) OTTIMISTICA con rollback, contatore, empty state,
 *            totale display-hint ricalcolato dalle righe acquistabili rimaste
 *            (mai autoritativo — la verità sta al checkout, §2.5). Focus
 *            management alla rimozione (row-focus). CTA checkout nascosta
 *            quando non resta nulla di acquistabile.
 */

import { isAbortError } from '../wishlist/server-driver';
import { focusBeforeRowRemoval } from './row-focus';
import type { CartServerDriver } from './server-driver';
import type { CartChangedAction, CartLabels } from './types';

export interface CartPageContext {
    driver: CartServerDriver;
    labels: CartLabels;
    announce: (message: string) => void;
    getCount: () => number;
    emit: (action: CartChangedAction, count: number, id?: string) => void;
    wishlistResync: () => Promise<void>;
}

/** Formato SSR dell'organo: number_format(x, 2, ',', '.') → parity client. */
function formatEur(value: number): string {
    return '€' + value.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function resolveCountText(count: number, labels: CartLabels): string {
    if (count === 1) {
        return labels.countOne;
    }
    return labels.countOther.replace(':count', String(count));
}

export function initCartPage(ctx: CartPageContext): void {
    const root = document.querySelector<HTMLElement>('[data-cart-page]');
    if (!root) {
        return; // non siamo sulla pagina carrello
    }

    const list = root.querySelector<HTMLElement>('[data-cart-list]');
    const empty = root.querySelector<HTMLElement>('[data-cart-empty]');
    const countEl = root.querySelector<HTMLElement>('[data-cart-page-count]');
    const heading = root.querySelector<HTMLElement>('[data-cart-heading]');
    const totalWrap = root.querySelector<HTMLElement>('[data-cart-total-wrap]');
    const totalEl = root.querySelector<HTMLElement>('[data-cart-total]');
    const checkoutCta = root.querySelector<HTMLElement>('[data-cart-checkout-cta]');

    if (!list || !empty || !countEl) {
        return; // markup incompleto → modulo spento (shell SSR resta leggibile)
    }

    function rows(): HTMLElement[] {
        return Array.from((list as HTMLElement).querySelectorAll<HTMLElement>('[data-cart-item]'));
    }

    /** Ricalcola contatore, empty state, totale e CTA dalle righe presenti. */
    function syncFromRows(): void {
        const current = rows();
        const count = current.length;

        // Gotcha organo: visibilità via display, MAI attributo hidden con utility flex.
        (empty as HTMLElement).style.display = count === 0 ? '' : 'none';
        (countEl as HTMLElement).style.display = count === 0 ? 'none' : '';
        if (count > 0) {
            (countEl as HTMLElement).textContent = resolveCountText(count, ctx.labels);
        }

        // Totale display-hint: somma dei prezzi LIVE delle sole righe acquistabili (§2.5)
        let total = 0;
        let purchasable = 0;
        for (const row of current) {
            const price = Number.parseFloat(row.dataset.itemPrice ?? '');
            if (row.dataset.itemPurchasable === '1' && Number.isFinite(price)) {
                total += price;
                purchasable++;
            }
        }
        if (totalEl) {
            totalEl.textContent = formatEur(Math.round(total * 100) / 100);
        }
        if (totalWrap) {
            totalWrap.style.display = purchasable > 0 ? '' : 'none';
        }
        if (checkoutCta) {
            checkoutCta.style.display = purchasable > 0 ? '' : 'none';
        }
    }

    /** "Torna in wishlist" (T2): ottimistico con rollback della riga. */
    async function onRemoveClick(button: HTMLElement): Promise<void> {
        const id = button.dataset.egiId ?? '';
        const row = button.closest<HTMLElement>('[data-cart-item]');
        if (id === '' || !row || button.getAttribute('aria-busy') === 'true') {
            return;
        }

        const previousCount = ctx.getCount();
        const parent = row.parentElement;
        const anchor = row.nextElementSibling;

        button.setAttribute('aria-busy', 'true'); // mai disabled: il focus resta (§7.5 M-373)
        button.style.opacity = '0.5';
        try {
            // Focus PRIMA di staccare il nodo (WCAG 2.2 §2.4.3 Focus Order, A)
            focusBeforeRowRemoval(list as HTMLElement, row, '[data-cart-item]', '[data-cart-remove]', heading);
            row.remove();
            syncFromRows();
            ctx.emit('remove', Math.max(0, previousCount - 1), id);

            const result = await ctx.driver.remove(id);
            ctx.emit('sync', result.cartCount, id); // conteggio autoritativo dal server
            ctx.announce(result.message ?? ctx.labels.removed);
            await ctx.wishlistResync(); // la riga è TORNATA in wishlist: secondo badge
        } catch (error) {
            if (isAbortError(error)) {
                return;
            }
            parent?.insertBefore(row, anchor); // rollback: la riga torna dov'era
            syncFromRows();
            ctx.emit('sync', previousCount, id);
            ctx.announce(ctx.labels.error);
        } finally {
            button.removeAttribute('aria-busy');
            button.style.opacity = '';
        }
    }

    // Click delegato sul root pagina per i bottoni "Torna in wishlist".
    root.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        const button = target.closest<HTMLElement>('[data-cart-remove]');
        if (button) {
            event.preventDefault();
            void onRemoveClick(button);
        }
    });

    syncFromRows();
}
